import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// utils
import useAuth from 'src/hooks/useAuth';
import { PATH_CHAT } from 'src/routes/paths';
import { ChatWindow, ChatSidebar } from 'src/sections/@chats/chat';
import ChatInfoSidebar from 'src/sections/@chats/chat/ChatInfoSidebar';
import { getChatInfoByChatId, getChatsAtendimento, getChatsPendente, getCountsChats, getListaFunil, getListaTags, getMensagensWhatsappDb, setChatInfo, setMessage } from 'src/redux/slices/chat';
// redux
import { setLimparChatStaff, setMessages } from 'src/redux/slices/staff';
// hooks
import { useDispatch, useSelector } from 'src/redux/store';
//services
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import { getConfig } from 'src/redux/slices/global';
import EmptyContentChat from 'src/components/EmptyContentChat';
import { Card } from 'src/@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { verifyFeatures } from 'src/utils/functions';
import CommentThread from 'src/sections/@chats/comments/CommentThread';

export default function Chat() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { conversationKey } = useParams();
  const { user, cliente } = useAuth();
  const { chatInfo, loadingPesquisarChat, mensagensArquivos, mensagensEnviar } = useSelector((state) => state.chat);
  const { config } = useSelector((state) => state.global);
  const { activeChatTab, selectedPost } = useSelector((state) => state.socialComments);
  const { t } = useTranslation('chat');
  // Modo comentarios: a aba Comentarios troca o painel direito pela thread de
  // comentarios — sem tocar no chatInfo (voltar a Conversas restaura o chat).
  // Gateado pela feature "socialmedia.comments" do cliente, como no ChatSidebar.
  const isCommentsMode = verifyFeatures(cliente?.features, 'socialmedia.comments') && activeChatTab === 'comentarios';
  const [listaClientesCanais, setListaClientesCanais] = useState([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [sidebarLeft, setSidebarLeft] = useState(false);
  const [openInfoSidebar, setOpenInfoSidebar] = useState(true);

  const messagesMergeArquivos = useMemo(() => {
    // Só pendentes desta conversa — concatenar mensagensEnviar inteiro vazava
    // preview de outro chat na sidebar de arquivos.
    const chatId = chatInfo?._id ? String(chatInfo._id) : null;
    const pendentes = (mensagensEnviar || []).filter(
      (msg) => chatId && msg?.chat_id && String(msg.chat_id) === chatId
    );
    return (mensagensArquivos || []).concat(pendentes).reverse();
  }, [mensagensArquivos, mensagensEnviar, chatInfo?._id]);

  // ---- Sincronizacao entre a URL e o chat aberto -------------------------------
  // O chat aberto vive so no redux (chatInfo), entao a URL nunca refletia qual
  // conversa estava na tela — impossivel compartilhar link ou recarregar sem perder
  // o chat. A rota `chats/chat/:conversationKey` ja existia (routes/index.js) mas
  // nao era usada por esta pagina.
  //
  // Feito aqui, num unico lugar, de proposito: as conversas sao abertas por varias
  // listas diferentes (Atendimento, Geral, Finalizado, Pesquisas, Arquivadas...) e
  // todas passam por `setChatInfo`. Reagir ao chatInfo cobre todas sem tocar em
  // nenhuma delas.

  // Marca que o chat atual foi carregado a partir da URL — so nesse caso precisamos
  // buscar as mensagens aqui (ao clicar numa lista, ela mesma ja as carrega).
  // Inicializado com o id da URL: no mount o chatInfo ainda e null, e sem isso o
  // efeito abaixo limparia a URL antes do deep link chegar a ser carregado.
  const chatVindoDaUrl = useRef(conversationKey && conversationKey !== 'new' ? conversationKey : null);

  // URL <- chat aberto. `replace` para nao encher o historico a cada troca de chat.
  //
  // `conversationKey` precisa estar nas dependencias: navegar pelo historico muda so a
  // URL, sem mexer no chatInfo. Sem ela, voltar de /chats/chat/A para /chats/chat
  // deixava a URL sem id com o chat A ainda aberto — e recarregar ali perdia o chat,
  // exatamente o que esta sincronizacao existe para evitar.
  useEffect(() => {
    const chatId = chatInfo?._id;
    if (chatId) {
      if (chatId !== conversationKey) {
        navigate(`${PATH_CHAT.chat.root}/${chatId}`, { replace: true });
      }
      return;
    }
    // Sem chat aberto (ex.: Esc ou voltar no mobile): limpa a URL — exceto se ainda
    // ha um carregamento em curso disparado pela propria URL.
    if (conversationKey && conversationKey !== 'new' && chatVindoDaUrl.current !== conversationKey) {
      navigate(PATH_CHAT.chat.root, { replace: true });
    }
  }, [chatInfo?._id, conversationKey]);

  // chat <- URL (link direto, F5 ou navegacao pelo historico).
  useEffect(() => {
    if (!conversationKey || conversationKey === 'new') return;
    if (chatInfo?._id === conversationKey) return;
    chatVindoDaUrl.current = conversationKey;
    dispatch(getChatInfoByChatId(conversationKey, null, user.id, true));
  }, [conversationKey]);

  // getChatInfoByChatId so preenche o chatInfo; as mensagens vem depois. Reusa
  // getMensagensWhatsappDb, que ja despacha para o endpoint certo conforme o canal
  // (whatsapp / instagram / messenger).
  useEffect(() => {
    if (!chatInfo?._id || chatVindoDaUrl.current !== chatInfo._id) return;
    chatVindoDaUrl.current = null;
    dispatch(getMensagensWhatsappDb(chatInfo.sessao, chatInfo.numeroCliente));
  }, [chatInfo?._id]);




  async function buscarListaClientesCanais() {
    try {
      const response_clientes_canais = await actionListaClientesCanais(cliente._id)
      setListaClientesCanais(response_clientes_canais.data.data);
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    dispatch(getChatsAtendimento(user.clienteId, user.id, user.setorId, 10, true));
    dispatch(getChatsPendente(user.clienteId, user.id, user.setorId));
    dispatch(getListaFunil(user.clienteId));
    let querys = `?cliente_id=${user.clienteId}`
    dispatch(getListaTags(querys))
    dispatch(getCountsChats(user.clienteId, user.id, user.setorId));
    dispatch(setLimparChatStaff());
    dispatch(getConfig());
    if (cliente.multiSession === 1) {
      buscarListaClientesCanais()
    }

    const handleKeyPress = (event) => {
      // Verifica se a tecla pressionada é a tecla "Esc" (código 27)
      if (event.keyCode === 27) {
        // Chama sua função aqui
        dispatch(setChatInfo(null))
        dispatch(setMessages([]))
      }
    };

    // Adiciona um ouvinte de eventos ao documento para capturar pressionamentos de teclas
    document.addEventListener('keydown', handleKeyPress);

    // Remove o ouvinte de eventos ao desmontar o componente
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };

  }, []);


  // Verificar modo de layout
  const [layoutMode, setLayoutMode] = useState(() => {
    return localStorage.getItem('layoutMode') || 'sidebar';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const newMode = localStorage.getItem('layoutMode') || 'sidebar';
      setLayoutMode(newMode);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('layoutModeChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('layoutModeChange', handleStorageChange);
    };
  }, []);

  // mt-14 apenas para mobile E apenas quando navbar está ativo (sidebar já tem seu próprio header)
  const shouldAddMarginTop = layoutMode === 'navbar';

  return (
    <div className="h-full flex bg-neutral-100 dark:bg-[#111214] overflow-hidden">
      <div className="flex-1 flex gap-2 p-1 md:p-3 overflow-hidden">
        {/* Card 1 — Chat list */}
        {(!sidebarLeft && (!isMobile || (isCommentsMode ? !selectedPost : !chatInfo))) && (
          <Card className="w-full min-w-0 md:w-[400px] md:min-w-[400px] flex-shrink-0 p-0 flex flex-col">
            {/* Busca, ações e "criar" agora vivem dentro do ChatSidebar, para
                ficarem na mesma linha em vez de espremer os rótulos das abas. */}
            <ChatSidebar />
          </Card>
        )}

        {/* Card 2 — Conversation / thread de comentarios */}
        {(!isMobile || (isCommentsMode ? selectedPost : chatInfo)) && (
          <Card className="flex-1 relative min-w-0">
            {isCommentsMode ? (
              selectedPost ? (
                <CommentThread />
              ) : (
                <EmptyContentChat title={t('comments.selectPost')} />
              )
            ) : chatInfo ? (
              <div className="absolute inset-0 flex flex-col">
                <div className="flex-1 flex flex-row min-h-0">
                  <ChatWindow
                    layoutMode="sidebar"
                    sidebarLeft={sidebarLeft}
                    changeOpenSidebarLeft={() => {
                      if (isMobile) {
                        dispatch(setChatInfo(null));
                        dispatch(setMessages([]));
                        setSidebarLeft(false);
                      } else {
                        setSidebarLeft(!sidebarLeft);
                      }
                    }}
                    config={config}
                    chatInfo={chatInfo}
                    hideSidebar
                    onSidebarToggle={() => setOpenInfoSidebar(!openInfoSidebar)}
                  />
                </div>
              </div>
            ) : (
              <EmptyContentChat title="Selecione um chat para começar" />
            )}
          </Card>
        )}

        {/* Card 3 — Chat Info Sidebar */}
        {!isCommentsMode && chatInfo && openInfoSidebar && !isMobile && (
          <Card className="w-[320px] min-w-[320px] flex-shrink-0">
            <ChatInfoSidebar chat={chatInfo} messagesWpp={messagesMergeArquivos} />
          </Card>
        )}
      </div>
    </div>
  );
}
